import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Button, Container, Divider, Stack } from "@mui/material";
import PageHeader from "@/components/PageHeader";
import GoogleConnection from "./components/google-connection/GoogleConnection";
import { useAppSelector } from "@/hooks";
import useRoutes from "@/hooks/useRoutes";
import { useAccessControl } from "@/features/access-control";
import { organizationById } from "@/features/organizations/slice";
import WebsiteMetadata, { MetadataSchema } from "@/pages/organization-website/components/WebsiteMetadata";
import { useUpdateWebsiteMutation } from "@/features/organizations/api/websiteApi";
import { Organization } from "@/types";
import { MenuItem, MetaDataWebsite } from "@/types/website";
import useSnackbar from "@/hooks/useSnackbar";
import TemplateSettings from "@/pages/organization-website/components/template-settings/TemplateSettings";
import { useFetchOrganizationWebsiteByIdQuery } from "@/features/organizations/api/organizationsApi";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useFetchTemplateByIdQuery } from "@/api/templatesApi";
import { TemplateSetting } from "./components/template-settings/components/TemplateSettingField";
import WebsiteAccess from "./components/WebsiteAccess";

export interface OrganizationWebsite {
  metaData: MetaDataWebsite;
  menu: MenuItem[];
  organization?: Organization;
  settings?: Record<string, TemplateSetting>;
}

export const OrganizationWebsiteSchema = Yup.object().shape({
  metaData: MetadataSchema,
});

const OrganizationWebsite = () => {
  const [navigate] = useRoutes();
  const container = useAppSelector((state) => state.ui?.container);
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const { organizationId } = useParams<{ organizationId: string }>();
  const org = useAppSelector(organizationById(organizationId ?? "")) as unknown as Organization;

  const [updateWebsite] = useUpdateWebsiteMutation();

  const websiteQuery = useFetchOrganizationWebsiteByIdQuery(
    {
      organizationId: organizationId || skipToken,
      websiteId: org?.website || skipToken,
    },
    {
      skip: !organizationId || !org?.website,
    }
  );

  const templateQuery = useFetchTemplateByIdQuery(websiteQuery?.data?.template || skipToken, {
    skip: !websiteQuery?.data?.template,
  });

  const handleSubmit = async (values: OrganizationWebsite) => {
    if (!organizationId || !websiteQuery?.data) return;

    try {
      await updateWebsite({
        organizationId,
        websiteId: websiteQuery.data.id,
        website: {
          metaData: values.metaData,
          settings: values.settings,
        },
      });
      openSnackbar("Sitio web actualizado", "success");
    } catch (error) {
      openSnackbar("Error al actualizar el sitio web", "error");
      console.error(error);
    }
  };

  const initialState = useMemo(() => {
    const initialValues = {
      metaData: websiteQuery?.data?.metaData,
      menu: [
        {
          label: "Inicio",
          url: "/",
        },
      ],
      organization: org,
      settings: websiteQuery?.data?.settings ?? templateQuery?.data?.settings ?? [],
    };

    if (websiteQuery?.data?.menu?.length) {
      initialValues.menu = websiteQuery?.data?.menu;
    }

    if (websiteQuery?.data) return initialValues;
  }, [org, websiteQuery?.data, templateQuery?.data]);

  return (
    <Formik enableReinitialize initialValues={initialState} validationSchema={OrganizationWebsiteSchema} onSubmit={handleSubmit}>
      <Form>
        <Stack spacing={4} pb={4}>
          <PageHeader
            title="Sitio Web"
            action={
              <Stack spacing={2} direction={"row"}>
                {org?.website ? (
                  <Button
                    size="large"
                    type="submit"
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      navigate("organizations.websites.read", {
                        websiteId: String(org?.website),
                      });
                    }}
                  >
                    Ver
                  </Button>
                ) : null}
                {hasAccess("update.website") ? (
                  <Button
                    size="large"
                    type="submit"
                    color="success"
                    onClick={() => {
                      //
                    }}
                  >
                    Guardar
                  </Button>
                ) : null}
              </Stack>
            }
          />
          <Stack>
            <Container
              maxWidth={container === 'fixed' ? 'lg' : false}
              sx={{
                height: '100%',
              }}
            >
              <TemplateSettings />
              <Divider>SEO - Optimización para Motores de Búsqueda</Divider>
              <WebsiteAccess />
              <WebsiteMetadata />
              <GoogleConnection />
            </Container>
          </Stack>
        </Stack>
      </Form>
    </Formik>
  );
};

export default OrganizationWebsite;
